import { graphql, useFragment } from 'react-relay';
import { useUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { getIsFreeShipping } from '../helpers/shippingHelpers.ts';

import { type useShippingPriceV2_viewer$key } from './__generated__/useShippingPriceV2_viewer.graphql.ts';
import { type useShippingPriceV2_item$key } from './__generated__/useShippingPriceV2_item.graphql.ts';

const viewerFragment = graphql`
    fragment useShippingPriceV2_viewer on Viewer {
        ...shippingHelpers_viewer
    }
`;

const itemFragment = graphql`
    fragment useShippingPriceV2_item on Item
    @argumentDefinitions(
        buyerId: { type: "String", defaultValue: "" }
        fetchShippingCostInfo: { type: "Boolean", defaultValue: false }
        fetchLiveShipmentQuote: { type: "Boolean", defaultValue: true }
        userZipCode: { type: "String", defaultValue: "" }
        userCountryCode: { type: "String", defaultValue: "" }
        itemQuantity: { type: "Int" }
    ) {
        ...shippingHelpers_item
        relevantShippingInfo(
            buyerId: $buyerId
            useLiveEndpoint: $fetchLiveShipmentQuote
            quantity: $itemQuantity
            zipCode: $userZipCode
            countryCode: $userCountryCode
        ) @include(if: $fetchShippingCostInfo) {
            shipmentQuote {
                buyerTotalAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
        }
    }
`;

export const useShippingPrice = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
}: {
    viewer: useShippingPriceV2_viewer$key | null | undefined;
    item: useShippingPriceV2_item$key | null | undefined;
    currency: string;
}): number | null => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const userRegions = useUserSessionRegions();

    if (!item) {
        return null;
    }

    const relevantQuote = item.relevantShippingInfo?.shipmentQuote;

    const { amount } =
        relevantQuote?.buyerTotalAmount?.convertedAmountList?.find(p => p?.currency === currency) ||
        {};

    if (typeof amount === 'number') {
        return amount;
    } else if (getIsFreeShipping({ viewer, item, userRegions })) {
        return 0;
    }

    return null;
};
